import { FC, useCallback, useEffect, useState } from 'react';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import {
  Accordion,
  AccordionItem,
  Button,
  ComboBox,
  DataTableSkeleton,
  Link as CarbonLink,
  Tag,
} from '@carbon/react';
import { useAtom } from 'jotai';
import { PageHeader } from 'components/page-header';
import { StatsBar } from 'components/stats-bar';
import { DataTable } from 'components/data-table';
import { subscribe } from 'lib/pusher';
import request from 'lib/request';
import { format0 } from 'utils/number';
import { runAuditLoadableAtom } from './atoms';
import type {
  AuditStatus,
  CheckName,
  CheckResultsResponse,
  CheckState,
  CheckStatus,
  PackageOption,
  RunAuditResponse,
  StaleRecord,
} from './types';
import styles from './page.module.scss';

// ─── Column definitions ───────────────────────────────────────────────────────

const col = createColumnHelper<StaleRecord>();

const subtractIdColumn = col.accessor('subtractId', {
  header: 'Subtract ID',
  size: 200,
  cell: (info) => {
    const row = info.row.original;
    const id = info.getValue();
    if (!id || !row.subtractHref) return id ?? '--';
    return (
      <CarbonLink
        href={row.subtractHref as string}
        target="_blank"
        rel="noopener noreferrer"
      >
        {id}
      </CarbonLink>
    );
  },
});

const subtractNameColumn = col.accessor('subtractName', {
  header: 'Subtract Name',
  size: 200,
  cell: (info) => info.getValue() ?? '--',
});

const daIdColumn = col.accessor('daId', {
  header: 'DA ID',
  size: 100,
});

const daNameColumn = col.accessor('daName', {
  header: 'DA Name',
  size: 200,
  cell: (info) => {
    const name = info.getValue();
    const daId = info.row.original.daId;
    if (!name || !daId) return name ?? '--';
    return (
      <CarbonLink
        href={`/development_areas/${daId}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {name}
      </CarbonLink>
    );
  },
});

const wellUwiColumn = col.accessor('wellUwi', {
  header: 'Well UWI',
  size: 180,
  cell: (info) => {
    const uwi = info.getValue();
    if (!uwi) return '--';
    return (
      <CarbonLink
        href={`/wells/${uwi}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {uwi}
      </CarbonLink>
    );
  },
});

const tirOrphanedUnitColumns = [
  col.accessor('id', { header: 'TIR ID', size: 100 }),
  subtractIdColumn,
  subtractNameColumn,
  daIdColumn,
  daNameColumn,
];

const tirOrphanedUatColumns = [
  col.accessor('id', { header: 'TIR ID', size: 100 }),
  subtractIdColumn,
  subtractNameColumn,
  col.accessor('uatId', { header: 'UAT ID', size: 100 }),
];

const intersectingNameColumn = col.accessor('intersectingName', {
  header: 'Intersecting Name',
  size: 220,
  cell: (info) => {
    const name = info.getValue();
    const row = info.row.original;
    if (!name) return '--';
    if (row.intersectingType === 'DevelopmentArea' && row.intersectingId) {
      return (
        <CarbonLink
          href={`/development_areas/${row.intersectingId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {name}
        </CarbonLink>
      );
    }
    return name;
  },
});

const tirOrphanedSubtractColumns = [
  col.accessor('id', { header: 'TIR ID', size: 100 }),
  subtractIdColumn,
  subtractNameColumn,
  col.accessor('intersectingType', { header: 'Intersecting Type', size: 160 }),
  col.accessor('intersectingId', { header: 'Intersecting ID', size: 120 }),
  intersectingNameColumn,
];

const tirSpatiallyStaleColumns = [
  col.accessor('id', { header: 'TIR ID', size: 100 }),
  subtractIdColumn,
  subtractNameColumn,
  daIdColumn,
  daNameColumn,
  col.accessor('included', { header: 'Included', size: 100 }),
  col.accessor('nonIntersecting', { header: 'Non-Intersecting', size: 140 }),
];

const nriOrphanedUnitColumns = [
  col.accessor('id', { header: 'NRI ID', size: 100 }),
  subtractIdColumn,
  subtractNameColumn,
  daIdColumn,
  daNameColumn,
  wellUwiColumn,
];

const nriOrphanedUatColumns = [
  col.accessor('id', { header: 'NRI ID', size: 100 }),
  subtractIdColumn,
  subtractNameColumn,
  col.accessor('uatId', { header: 'UAT ID', size: 100 }),
  wellUwiColumn,
];

const nriBaseColumns = [
  col.accessor('id', { header: 'NRI ID', size: 100 }),
  subtractIdColumn,
  subtractNameColumn,
  daIdColumn,
  daNameColumn,
  wellUwiColumn,
];

const nriOrphanedWellColumns = nriBaseColumns;
const nriNoTirBackingColumns = nriBaseColumns;

const nriWellUnitMismatchColumns = nriBaseColumns;
const nriUatWellMismatchColumns = [
  col.accessor('id', { header: 'NRI ID', size: 100 }),
  subtractIdColumn,
  subtractNameColumn,
  col.accessor('objectType', { header: 'Object Type', size: 160 }),
  daIdColumn,
  daNameColumn,
  col.accessor('uatId', { header: 'UAT ID', size: 100 }),
  wellUwiColumn,
];
const nriNullUatWithAmColumns = [
  col.accessor('id', { header: 'NRI ID', size: 100 }),
  subtractIdColumn,
  subtractNameColumn,
  col.accessor('objectType', { header: 'Object Type', size: 160 }),
  daIdColumn,
  daNameColumn,
  wellUwiColumn,
];

const nriNullUatSupersededColumns = [
  ...nriBaseColumns,
  col.accessor('nullCreatedAt', {
    header: 'Stale (Null UAT) Created',
    size: 200,
    meta: { type: 'datetime' },
  }),
  col.accessor('supersedingCreatedAt', {
    header: 'Valid (With UAT) Created',
    size: 200,
    meta: { type: 'datetime' },
  }),
];

const nriNullWellSupersededColumns = [
  col.accessor('id', { header: 'NRI ID', size: 100 }),
  subtractIdColumn,
  subtractNameColumn,
  daIdColumn,
  daNameColumn,
  col.accessor('slotId', { header: 'Slot ID', size: 100 }),
  col.accessor('nullCreatedAt', {
    header: 'Stale (Null Well) Created',
    size: 200,
    meta: { type: 'datetime' },
  }),
  col.accessor('supersedingCreatedAt', {
    header: 'Valid (With Well) Created',
    size: 200,
    meta: { type: 'datetime' },
  }),
];

const nriOrphanedSubtractColumns = nriBaseColumns;

// ─── New phase column definitions ────────────────────────────────────────────

const uwaBaseColumns = [
  col.accessor('id', { header: 'UWA ID', size: 100 }),
  wellUwiColumn,
  daNameColumn,
];

const uwaOrphanedWellColumns = uwaBaseColumns;
const uwaOrphanedDaColumns = uwaBaseColumns;

const amOrphanedAssignmentColumns = [
  col.accessor('id', { header: 'AM ID', size: 100 }),
  col.accessor('uwaId', { header: 'Assignment ID', size: 140 }),
];

const amliOrphanedUatColumns = [
  col.accessor('id', { header: 'AMLI ID', size: 100 }),
  col.accessor('allocationManagerId', {
    header: 'Alloc Manager ID',
    size: 150,
  }),
  col.accessor('uatId', { header: 'UAT ID', size: 100 }),
];

const uiaOrphanedWellColumns = [
  col.accessor('id', { header: 'UIA ID', size: 100 }),
  wellUwiColumn,
  daIdColumn,
  daNameColumn,
];

const uiaOrphanedUnitColumns = [
  col.accessor('id', { header: 'UIA ID', size: 100 }),
  wellUwiColumn,
  daIdColumn,
];

const slotOrphanedWellColumns = [
  col.accessor('id', { header: 'Slot ID', size: 100 }),
  wellUwiColumn,
  daIdColumn,
  daNameColumn,
];

const scarDuplicateSubtractColumns = [
  col.accessor('id', { header: 'SCAR ID', size: 100 }),
  col.accessor('targetGeometryName', { header: 'Subtract Name', size: 160 }),
  col.accessor('targetGeometryType', { header: 'Type', size: 100 }),
  col.accessor('updatedAt', { header: 'Updated At', size: 180 }),
];

const COLUMNS_BY_CHECK = {
  tir_orphaned_unit: tirOrphanedUnitColumns,
  tir_orphaned_uat: tirOrphanedUatColumns,
  tir_orphaned_subtract: tirOrphanedSubtractColumns,
  tir_spatially_stale: tirSpatiallyStaleColumns,
  nri_orphaned_unit: nriOrphanedUnitColumns,
  nri_orphaned_uat: nriOrphanedUatColumns,
  nri_orphaned_well: nriOrphanedWellColumns,
  nri_no_tir_backing: nriNoTirBackingColumns,
  nri_well_unit_mismatch: nriWellUnitMismatchColumns,
  nri_uat_well_mismatch: nriUatWellMismatchColumns,
  nri_null_uat_with_am: nriNullUatWithAmColumns,
  nri_null_uat_superseded: nriNullUatSupersededColumns,
  nri_null_well_superseded: nriNullWellSupersededColumns,
  nri_orphaned_subtract: nriOrphanedSubtractColumns,
  uwa_orphaned_well: uwaOrphanedWellColumns,
  uwa_orphaned_da: uwaOrphanedDaColumns,
  am_orphaned_assignment: amOrphanedAssignmentColumns,
  amli_orphaned_uat: amliOrphanedUatColumns,
  uia_orphaned_well: uiaOrphanedWellColumns,
  uia_orphaned_unit: uiaOrphanedUnitColumns,
  slot_orphaned_well: slotOrphanedWellColumns,
  scar_duplicate_subtract: scarDuplicateSubtractColumns,
};

// ─── Check metadata ───────────────────────────────────────────────────────────

type Phase = 'tir' | 'nri' | 'uwa' | 'am' | 'uia' | 'slot' | 'scar';

const CHECK_META: Array<{
  name: CheckName;
  label: string;
  phase: Phase;
  description: string;
}> = [
  {
    name: 'tir_orphaned_unit',
    label: 'Orphaned Units',
    phase: 'tir',
    description:
      'TIRs referencing a DevelopmentArea that no longer exists. Caused by unit deletion without cleanup. These TIRs will produce incorrect intersection data.',
  },
  {
    name: 'tir_orphaned_uat',
    label: 'Orphaned UATs',
    phase: 'tir',
    description:
      'TIRs referencing a UnitAllocationTract that no longer exists. Caused by UAT deletion without cleanup. May produce stale allocation data.',
  },
  {
    name: 'tir_orphaned_subtract',
    label: 'Orphaned Subtracts',
    phase: 'tir',
    description:
      'TIRs referencing a Subtract that was soft-deleted in Salesforce. Caused by sync deleting the subtract without triggering TIR cleanup. Wastes storage and may confuse downstream NRI.',
  },
  {
    name: 'tir_spatially_stale',
    label: 'Spatially Stale',
    phase: 'tir',
    description:
      'TIRs where the subtract and development area geometries no longer spatially intersect. Geometry changed after the TIR was created but TIA did not re-run to update or remove the TIR.',
  },
  {
    name: 'nri_orphaned_unit',
    label: 'Orphaned Units',
    phase: 'nri',
    description:
      'NRI calculations referencing a DevelopmentArea that no longer exists. Caused by unit deletion without NRI cleanup. These NRIs are invalid and should be removed.',
  },
  {
    name: 'nri_orphaned_uat',
    label: 'Orphaned UATs',
    phase: 'nri',
    description:
      'NRI calculations referencing a UnitAllocationTract that no longer exists. Likely caused by UAT deletion during reallocation. The NRI should be recalculated without this UAT.',
  },
  {
    name: 'nri_orphaned_well',
    label: 'Orphaned Wells',
    phase: 'nri',
    description:
      'NRI calculations referencing a well UWI that no longer exists in the wells table. Caused by well deletion without NRI cleanup. These are invalid calculations.',
  },
  {
    name: 'nri_no_tir_backing',
    label: 'No TIR Backing',
    phase: 'nri',
    description:
      'NRI calculations for a unit/subtract combination that has no corresponding included TIR. The spatial intersection was removed or excluded, but the NRI persists. Recalculation should remove these.',
  },
  {
    name: 'nri_well_unit_mismatch',
    label: 'Well/Unit Mismatch',
    phase: 'nri',
    description:
      "NRI calculations where the well's current best-fit unit doesn't match the unit in the NRI record. Caused by a well being reassigned to a different unit without NRI recalculation.",
  },
  {
    name: 'nri_uat_well_mismatch',
    label: 'UAT/Well Mismatch',
    phase: 'nri',
    description:
      'NRI calculations referencing a UAT + well combination that is not in any AllocationManagerLineItem. The UAT exists but is no longer linked to this well via an allocation manager.',
  },
  {
    name: 'nri_null_uat_with_am',
    label: 'Null UAT with AM',
    phase: 'nri',
    description:
      'NRI calculations with a null UAT for a well that now has an AllocationManager. A UAT-based NRI should have been calculated instead. Indicates the NRI was computed before the allocation manager was created.',
  },
  {
    name: 'nri_null_uat_superseded',
    label: 'Null UAT Superseded',
    phase: 'nri',
    description:
      'NRI calculations with a null UAT where a valid NRI with a specific UAT exists for the same well/unit/subtract. Often the null-UAT record was created after the valid one, indicating a recalculation bug that recreated a stale record. The null-UAT version should be removed.',
  },
  {
    name: 'nri_null_well_superseded',
    label: 'Null Well Superseded',
    phase: 'nri',
    description:
      'NRI calculations with a null well UWI where a valid well-backed NRI exists for the same slot/unit/subtract. These are stale LTD calculations left behind when a slot transitions from LTD to well-backed (e.g., a permit gets drilled). The null-well version should be removed.',
  },
  {
    name: 'nri_orphaned_subtract',
    label: 'Orphaned Subtracts',
    phase: 'nri',
    description:
      'NRI calculations referencing a Subtract that was soft-deleted in Salesforce. These NRIs are for a subtract that no longer exists and should be removed.',
  },
  {
    name: 'uwa_orphaned_well',
    label: 'Orphaned Wells',
    phase: 'uwa',
    description:
      'Unit::WellAssignment records referencing a well UWI that no longer exists. Caused by well deletion without assignment cleanup. Prevents the well slot from being reassigned.',
  },
  {
    name: 'uwa_orphaned_da',
    label: 'Orphaned Dev Areas',
    phase: 'uwa',
    description:
      'Unit::WellAssignment records referencing a DevelopmentArea that no longer exists. Caused by unit deletion without assignment cleanup. The well needs reassignment via BestFit.',
  },
  {
    name: 'am_orphaned_assignment',
    label: 'Orphaned Assignments',
    phase: 'am',
    description:
      'AllocationManager records referencing a Unit::WellAssignment that no longer exists. Caused by well assignment deletion without AM cleanup. May produce incorrect allocation calculations.',
  },
  {
    name: 'amli_orphaned_uat',
    label: 'Orphaned UATs',
    phase: 'am',
    description:
      'AllocationManagerLineItem records referencing a UnitAllocationTract that no longer exists. Caused by UAT deletion without line item cleanup. Produces incorrect allocation splits.',
  },
  {
    name: 'uia_orphaned_well',
    label: 'Orphaned Wells',
    phase: 'uia',
    description:
      'UnitIntersectionAnalysis records referencing a well that no longer exists. Caused by well deletion without UIA cleanup. Wastes storage but has no functional impact.',
  },
  {
    name: 'uia_orphaned_unit',
    label: 'Orphaned Units',
    phase: 'uia',
    description:
      'UnitIntersectionAnalysis records referencing a DevelopmentArea that no longer exists. Caused by unit deletion without UIA cleanup. Wastes storage.',
  },
  {
    name: 'slot_orphaned_well',
    label: 'Orphaned Wells',
    phase: 'slot',
    description:
      'Formation Slot records referencing a well UWI that no longer exists. Caused by well deletion without slot cleanup. The slot appears occupied but the well is gone.',
  },
  {
    name: 'scar_duplicate_subtract',
    label: 'Duplicate Subtracts',
    phase: 'scar',
    description:
      'Duplicate SubtractCoverageAnalysisResult records sharing the same (name, type). Created by a race condition in concurrent Sidekiq workers. The older duplicate should be removed.',
  },
];

interface PhaseConfig {
  key: Phase;
  number: number;
  title: string;
}

const PHASES: PhaseConfig[] = [
  { key: 'tir', number: 1, title: 'TractIntersectionResult' },
  { key: 'nri', number: 2, title: 'NriCalculation' },
  { key: 'uwa', number: 3, title: 'UnitWellAssignment' },
  { key: 'am', number: 4, title: 'AllocationManager' },
  { key: 'uia', number: 5, title: 'UnitIntersectionAnalysis' },
  { key: 'slot', number: 6, title: 'Formation Slots' },
  { key: 'scar', number: 7, title: 'CoverageAnalysisResult' },
];

const CHECKS_BY_PHASE: Record<Phase, typeof CHECK_META> = Object.fromEntries(
  PHASES.map(({ key }) => [key, CHECK_META.filter((c) => c.phase === key)])
) as Record<Phase, typeof CHECK_META>;

const buildInitialChecks = (): Record<CheckName, CheckState> =>
  Object.fromEntries(
    CHECK_META.map(({ name }) => [
      name,
      { status: 'pending' as const, count: 0, records: [] as StaleRecord[] },
    ])
  ) as Record<CheckName, CheckState>;

// ─── Check accordion item ─────────────────────────────────────────────────────

const CheckAccordionItem: FC<{
  name: CheckName;
  label: string;
  description: string;
  state: CheckState;
}> = ({ name, label, description, state }) => {
  const titleSuffix =
    state.status === 'running'
      ? ' (running…)'
      : state.status === 'complete'
        ? ` (${format0(state.count)})`
        : state.status === 'error'
          ? ' (error)'
          : '';

  const numColumns = COLUMNS_BY_CHECK[name].length;

  return (
    <AccordionItem title={`${label}${titleSuffix}`} open={false}>
      <div className={styles.checkDescription}>{description}</div>
      {state.status === 'pending' && (
        <div className={styles.pendingState}>Waiting for audit to start…</div>
      )}
      {state.status === 'running' && (
        <DataTableSkeleton
          columnCount={numColumns}
          rowCount={5}
          size="xs"
          showHeader={false}
          showToolbar={false}
        />
      )}
      {state.status === 'error' && (
        <div className={styles.errorState}>{state.error ?? 'Step failed'}</div>
      )}
      {state.status === 'complete' && (
        <DataTable
          data={state.records}
          columns={COLUMNS_BY_CHECK[name] as ColumnDef<StaleRecord>[]}
          getRowId={(row) => String(row.id)}
          persistenceKey={`stale-audit-${name}`}
          config={{
            sorting: true,
            globalSearch: true,
            columnFilters: false,
            columnReordering: true,
            columnPinning: false,
            columnVisibility: true,
            export: true,
          }}
        />
      )}
    </AccordionItem>
  );
};

// ─── Phase section ───────────────────────────────────────────────────────────

const PhaseSection: FC<{
  phase: PhaseConfig;
  checks: Record<CheckName, CheckState>;
  isRunning: boolean;
}> = ({ phase, checks, isRunning }) => {
  const phaseChecks = CHECKS_BY_PHASE[phase.key];
  const total = phaseChecks.reduce(
    (sum, { name }) => sum + checks[name].count,
    0
  );

  return (
    <div className={styles.phase}>
      <div className={styles.phaseHeader}>
        <span className={styles.phaseTitle}>
          Phase {phase.number}: {phase.title}
        </span>
        <Tag type={total > 0 ? 'red' : 'green'} size="sm">
          {isRunning ? '…' : `${total} stale`}
        </Tag>
      </div>
      <Accordion>
        {phaseChecks.map(({ name, label, description }) => (
          <CheckAccordionItem
            key={name}
            name={name}
            label={label}
            description={description}
            state={checks[name]}
          />
        ))}
      </Accordion>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const StaleRecordsAuditPage: FC = () => {
  const [runState, triggerRun] = useAtom(runAuditLoadableAtom);

  const [auditStatus, setAuditStatus] = useState<AuditStatus>('idle');
  const [auditId, setAuditId] = useState<string | null>(null);
  const [auditChannel, setAuditChannel] = useState<string | null>(null);
  const [auditPackageName, setAuditPackageName] = useState<string | null>(null);
  const [grandTotal, setGrandTotal] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number | null>(null);
  const [checks, setChecks] =
    useState<Record<CheckName, CheckState>>(buildInitialChecks);

  const [packages, setPackages] = useState<PackageOption[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<PackageOption | null>(
    null
  );

  useEffect(() => {
    const abortController = new AbortController();
    request
      .post<{ results: PackageOption[] }, never>(
        '/queries/packages',
        { query: { page_size: 500 } },
        abortController
      )
      .then((res) => {
        if (res.type === 'success') setPackages(res.data.results);
      });
    return () => abortController.abort();
  }, []);

  const setCheckStatus = useCallback(
    (name: CheckName, status: CheckStatus, extra?: Partial<CheckState>) => {
      setChecks((prev) => ({
        ...prev,
        [name]: { ...prev[name], status, ...extra },
      }));
    },
    []
  );

  const fetchCheckResults = useCallback(
    async (currentAuditId: string, checkName: CheckName) => {
      setCheckStatus(checkName, 'running');
      const response = await request.get<CheckResultsResponse>(
        `/admin/stale_records_audit/results/${currentAuditId}?check=${checkName}`
      );
      if ('error' in response) {
        setCheckStatus(checkName, 'error', {
          error: (response as { error: string }).error,
        });
      } else {
        setCheckStatus(checkName, 'complete', {
          count: response.count,
          records: response.records,
        });
      }
    },
    [setCheckStatus]
  );

  useEffect(() => {
    if (!auditId || !auditChannel) return;

    const subscription = subscribe(auditChannel);

    subscription.bind(
      'check_complete',
      (data: { check: CheckName; count: number }) => {
        fetchCheckResults(auditId, data.check);
      }
    );

    subscription.bind(
      'check_error',
      (data: { check: CheckName; error: string }) => {
        setCheckStatus(data.check, 'error', { error: data.error });
      }
    );

    subscription.bind(
      'audit_complete',
      (data: { grand_total: number; elapsed_seconds: number }) => {
        setGrandTotal(data.grand_total);
        setElapsedSeconds(data.elapsed_seconds);
        setAuditStatus('complete');
        // Backfill results for any checks whose check_complete events were missed
        // (race condition: events can fire before the subscription is established).
        setChecks((prev) => {
          CHECK_META.forEach(({ name }) => {
            if (prev[name].status === 'pending')
              fetchCheckResults(auditId, name);
          });
          return prev;
        });
      }
    );

    subscription.bind('audit_error', () => setAuditStatus('error'));

    return () => {
      subscription.unbind_all?.();
    };
  }, [auditId, auditChannel, fetchCheckResults, setCheckStatus]);

  useEffect(() => {
    if (runState.state !== 'hasData' || !runState.data) return;
    const data = runState.data as RunAuditResponse;
    setAuditId(data.auditId);
    setAuditChannel(data.channel);
    setAuditPackageName(data.packageName ?? null);
    setAuditStatus('running');
    setGrandTotal(null);
    setElapsedSeconds(null);
    setChecks(buildInitialChecks());
  }, [runState]);

  const phaseTotals = Object.fromEntries(
    PHASES.map(({ key }) => [
      key,
      CHECKS_BY_PHASE[key].reduce(
        (sum, { name }) => sum + checks[name].count,
        0
      ),
    ])
  ) as Record<Phase, number>;
  const isRunning = auditStatus === 'running';

  return (
    <>
      <PageHeader breadcrumbs={[]} title="Stale Records Audit" actions={null} />

      <div className={styles.description}>
        <p>
          Scans key data tables for integrity issues — orphaned references to
          deleted Subtracts, DevelopmentAreas, UnitAllocationTracts, and Wells
          across TIRs, NRIs, Well Assignments, Allocation Managers, Intersection
          Analyses, Formation Slots, Subtract Geometries, and Evaluation
          Allocations.
        </p>
        <p className={styles.descriptionParagraph}>
          This is a read-only audit. No records are modified or deleted. Results
          can be exported from each check table below.
        </p>
      </div>

      <div className={styles.toolbar}>
        <ComboBox
          id="stale-audit-package-filter"
          titleText="Scope to package (optional)"
          placeholder="All packages"
          items={packages}
          itemToString={(item) => item?.name ?? ''}
          selectedItem={selectedPackage}
          onChange={({ selectedItem }) =>
            setSelectedPackage(selectedItem ?? null)
          }
          disabled={isRunning}
          className={styles.packageComboBox}
        />

        <Button
          kind="primary"
          size="md"
          disabled={isRunning || runState.state === 'loading'}
          onClick={() => triggerRun({ packageId: selectedPackage?.id ?? null })}
        >
          {isRunning ? 'Running…' : 'Run Audit'}
        </Button>

        {auditStatus === 'idle' && !selectedPackage && (
          <span className={styles.status}>
            Will scan all records system-wide.
          </span>
        )}
        {auditStatus === 'idle' && selectedPackage && (
          <span className={styles.status}>
            Will scan records for <strong>{selectedPackage.name}</strong>.
          </span>
        )}
        {auditStatus === 'running' && (
          <span className={styles.status}>
            Audit in progress
            {auditPackageName ? ` — ${auditPackageName}` : ' (all packages)'}…
          </span>
        )}
        {auditStatus === 'complete' && (
          <span className={styles.status}>
            Complete — {format0(grandTotal ?? 0)} stale record
            {grandTotal !== 1 ? 's' : ''} found in {elapsedSeconds}s
            {auditPackageName ? ` (${auditPackageName})` : ''}
          </span>
        )}
        {auditStatus === 'error' && (
          <span className={styles.error}>Job failed. Check Sidekiq logs.</span>
        )}
        {runState.state === 'hasError' && (
          <span className={styles.error}>
            Failed to start:{' '}
            {runState.error instanceof Error
              ? runState.error.message
              : 'Unknown error'}
          </span>
        )}
      </div>

      {auditStatus !== 'idle' && (
        <>
          <StatsBar
            stats={[
              {
                label: 'Grand Total',
                value: grandTotal ?? 0,
                tagType:
                  grandTotal === null
                    ? 'gray'
                    : grandTotal > 0
                      ? 'red'
                      : 'green',
              },
              ...PHASES.map(({ key, title }) => ({
                label: `Stale ${title}`,
                value: phaseTotals[key],
                tagType:
                  phaseTotals[key] > 0 ? ('red' as const) : ('green' as const),
              })),
            ]}
          />

          {PHASES.map((phase) => (
            <PhaseSection
              key={phase.key}
              phase={phase}
              checks={checks}
              isRunning={isRunning}
            />
          ))}
        </>
      )}
    </>
  );
};
